import React, { useState } from "react";
import {
  FaLock,
  FaEye,
  FaEyeSlash,
  FaUndo,
  FaCheck,
  FaGlassWhiskey,
  FaLeaf,
  FaCoffee,
  FaWeightHanging,
  FaBalanceScale,
} from "react-icons/fa";
import { TextInput, NumberInput, Checkbox, CustomDropdown } from "../../../atoms";
import { getTokens } from "./SettingsSectionShell";
import { applyGrinderLabelRename } from "./labelOverrides";

// Regex → Türkçe açıklama (SettingInput.js'ten alındı, genişletildi)
const getRegexDescription = (regex) => {
  if (!regex) return "Geçerli bir değer giriniz";
  switch (regex) {
    case "^[0-9]{6}$":
      return "6 haneli sayısal şifre olmalı";
    case "^[0-9]{6,10}$":
      return "6 ile 10 hane arasında sayısal şifre olmalı";
    case "^(404|000)$":
      return "Sadece 404 veya 000 girebilirsiniz";
    case "^.{1,30}$":
      return "1-30 karakter arasında olmalı";
    case "^.{1,50}$":
      return "1-50 karakter arasında olmalı";
    case "^.+$":
      return "Boş bırakılamaz";
    default: {
      let m;
      if ((m = regex.match(/^\^?\[0-9\]\{(\d+),(\d+)\}\$?$/))) {
        return `${m[1]} ile ${m[2]} hane arasında sayı olmalı`;
      }
      if ((m = regex.match(/^\^?\[0-9\]\{(\d+)\}\$?$/))) {
        return `${m[1]} haneli sayı olmalı`;
      }
      if ((m = regex.match(/^\^?\.\{(\d+),(\d+)\}\$?$/))) {
        return `${m[1]}-${m[2]} karakter arasında olmalı`;
      }
      if ((m = regex.match(/^\^?\(([^)]+)\)\$?$/))) {
        const opts = m[1].split("|").map((s) => `"${s}"`).join(", ");
        return `Sadece: ${opts}`;
      }
      return "Geçersiz format";
    }
  }
};

const isPasswordRegex = (regex) =>
  regex === "^[0-9]{6}$" || regex === "^[0-9]{6,10}$";

// Cihaz Checkbox degerini boolean'a normalize eder. Read payload bu alanlari
// bazen string ("0"/"false") veya sayi (0/1) olarak gonderebiliyor; ham "!!v"
// ile "0"/"false" gibi truthy string'ler daima acik gorunurdu (toggle gercek
// durumu yansitmazdi). MultiSelect'in defansif JSON.parse'i ile ayni mantik.
const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "1" || s === "true" || s === "on" || s === "acik" || s === "açık";
  }
  return !!v;
};

// Bilinen ID'ler icin Turkce label fallback'i — cihaz eski format
// (sade string array) gonderirse bile UI'da insan-okuyabilir gosterilir.
const KNOWN_ID_LABELS = {
  // Türk kahvesi düzenleme kapsamı (general.turkishCoffee.editScope)
  basit: "Basit",
  standart: "Standart",
  gelismis: "Gelişmiş",
  tr: "Türkçe",
  en: "İngilizce",
};

const formatDisplayValue = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Açık" : "Kapalı";
  if (Array.isArray(value)) {
    if (value.length === 0) return "(boş)";
    return value.map((v) => formatDisplayValue(v)).join(", ");
  }
  if (typeof value === "string") {
    // Bazı ayarlar (ör. GrinderMillTimes) JSON string olarak gelebiliyor —
    // düzenleme dalı da aynı savunmayı yapıyor.
    const trimmed = value.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === "object") return formatDisplayValue(parsed);
      } catch (_) {
        /* düz metin — aşağıda olduğu gibi gösterilir */
      }
    }
    return value;
  }
  if (typeof value === "object") {
    // Anahtar:değer taşıyan ayarlar (Türk Kahvesi Değirmen Süreleri gibi)
    // salt-okunur görünümde "(obje)" yazıyordu — okunur hale getirilir.
    const entries = Object.entries(value).filter(
      ([, v]) => v !== null && v !== undefined,
    );
    if (entries.length === 0) return "—";
    return entries
      .map(([k, v]) => `${k}: ${formatDisplayValue(v)}`)
      .join(" · ");
  }
  return String(value);
};

/**
 * V2 Settings Field Card — atom kullanan ortak field render.
 * Her setting tipini doğru atom ile render eder + değişti rozeti + hata gösterimi.
 *
 * Props:
 *  - setting: cihazdan gelen { type, label, value, options, lowerBound, upperBound, regex, disabled, cupSchema }
 *  - onChange: (newValue) => void
 *  - isChanged: değişti mi rozeti
 *  - originalValue: değişti ise eski değer
 *  - color: section temasıyla uyumlu border vurgusu
 *  - onRevert: (opsiyonel) tek tıkla orijinale döndürme handler
 *  - colSpan: "1" | "2" | "3" — Grid içinde col-span ayarı (MultiSelect gibi geniş alanlar için)
 */
export default function SettingsFieldCard({
  setting: rawSetting,
  onChange,
  isChanged = false,
  originalValue,
  color = "indigo",
  onRevert,
  colSpan = "1",
  // Sadece TeaFilterCups kartı: "Varsayılana Dön" aksiyonunu pending
  // change olarak kuyruklar (GeneralSection sağlar).
  onResetDefaults,
  // Sadece TeaFilterCups kartı: başlıkta, "Varsayılana Dön"ün yanında
  // gösterilecek sayısal alan(lar). Ağırlık kalibrasyon eşiği bardak
  // kayıtlarıyla birlikte ayarlandığı için ayrı kart yerine buraya gömülür.
  // Her eleman: { key, setting, value, onChange, isChanged, originalValue }
  inlineFields = [],
}) {
  const [showPassword, setShowPassword] = useState(false);
  const t = getTokens(color);
  const setting = applyGrinderLabelRename(rawSetting);
  const isDisabled = !!setting?.disabled;

  if (!setting) return null;

  const colSpanClass =
    colSpan === "3"
      ? "col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4"
      : colSpan === "2"
        ? "col-span-1 sm:col-span-2"
        : "col-span-1";

  const wrapperClass = [
    "relative rounded-xl border bg-white p-3 transition-all duration-200",
    isChanged
      ? "border-indigo-400 ring-2 ring-indigo-100 bg-indigo-50/30"
      : `border-gray-200 hover:${t.border}`,
    isDisabled ? "opacity-60" : "",
    colSpanClass,
  ].join(" ");

  // Label + altında atom
  const renderLabel = () => (
    <div className="flex items-start justify-between gap-2 mb-2">
      <div className="flex items-center gap-1.5 min-w-0">
        <label
          className="text-xs font-semibold text-gray-700 leading-tight truncate"
          title={setting.label}
        >
          {setting.label}
        </label>
        {isDisabled && <FaLock className="w-2.5 h-2.5 text-gray-400 shrink-0" />}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {isChanged && (
          <>
            {onRevert && (
              <button
                type="button"
                onClick={onRevert}
                className="text-gray-400 hover:text-indigo-600 transition"
                title="Geri al"
              >
                <FaUndo size={10} />
              </button>
            )}
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-indigo-100 text-indigo-700"
              title="Değiştirildi"
            >
              YENİ
            </span>
          </>
        )}
      </div>
    </div>
  );

  // -- Disabled (read-only display) --
  if (isDisabled) {
    // Karmaşık tipler düz metne çevrilince okunmaz oluyordu (bardak kayıtları
    // tek paragrafta İngilizce anahtarlarla akıyordu). Bu tipler düzenlenebilir
    // hâldeki yerleşimi taklit eden salt-okunur görünüm alır.
    if (setting.type === "TeaFilterCups") {
      const cups = Array.isArray(setting.value) ? setting.value : [];
      return (
        <div className={wrapperClass}>
          {renderLabel()}
          {cups.length === 0 ? (
            <div className="text-sm text-gray-400">—</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {cups.map((cup, idx) => (
                <div
                  key={cup.id || idx}
                  className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5"
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-xs font-SemiBold text-gray-700 truncate">
                      {cup.name || `Bardak ${idx + 1}`}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      {cup.referenceTea && (
                        <span className="text-[10px] font-SemiBold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                          Çay Ref.
                        </span>
                      )}
                      {cup.referenceFilterCoffee && (
                        <span className="text-[10px] font-SemiBold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                          Filtre Ref.
                        </span>
                      )}
                    </div>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
                    {[
                      ["Dolu Ağırlık", cup.filledWeight, "gr"],
                      ["Boş Ağırlık", cup.emptyWeight, "gr"],
                      ["Alt Tolerans", cup.lowerTolerance, "gr"],
                      ["Üst Tolerans", cup.upperTolerance, "gr"],
                    ].map(([label, value, unit]) => (
                      <div key={label} className="min-w-0">
                        <dt className="text-[10px] text-gray-400 truncate">
                          {label}
                        </dt>
                        <dd className="text-xs font-Medium text-gray-700">
                          {value === null || value === undefined
                            ? "—"
                            : `${value} ${unit}`}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Anahtar:değer taşıyan ayarlar (ör. Değirmen Süreleri) — satır satır.
    const objectValue =
      setting.value && typeof setting.value === "object" && !Array.isArray(setting.value)
        ? setting.value
        : null;
    if (objectValue) {
      const entries = Object.entries(objectValue).filter(
        ([, v]) => v !== null && v !== undefined && typeof v !== "object",
      );
      if (entries.length > 0) {
        return (
          <div className={wrapperClass}>
            {renderLabel()}
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
              {entries.map(([k, v]) => (
                <div key={k} className="min-w-0">
                  <dt className="text-[10px] text-gray-400 truncate">{k}</dt>
                  <dd className="text-xs font-Medium text-gray-700 truncate">
                    {formatDisplayValue(v)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        );
      }
    }

    return (
      <div className={wrapperClass}>
        {renderLabel()}
        <div className="text-sm font-semibold text-gray-500">
          {formatDisplayValue(setting.value)}
        </div>
      </div>
    );
  }

  // -- Number --
  if (setting.type === "Number") {
    const upper = setting.upperBound ?? setting.upperBounde;
    const lower = setting.lowerBound;
    const val = setting.value;
    let numberError = false;
    let numberErrorMsg = "";
    if (val === "" || val === null || val === undefined) {
      numberError = true;
      numberErrorMsg = "Değer boş bırakılamaz";
    } else if (typeof val !== "number" || Number.isNaN(val)) {
      numberError = true;
      numberErrorMsg = "Geçerli bir sayı girin";
    } else if (typeof lower === "number" && val < lower) {
      numberError = true;
      numberErrorMsg = `En az ${lower} olmalı`;
    } else if (typeof upper === "number" && val > upper) {
      numberError = true;
      numberErrorMsg = `En fazla ${upper} olmalı`;
    }
    const numberWrapperClass = numberError
      ? wrapperClass.replace(
          "border-gray-200",
          "border-red-400 ring-2 ring-red-100 bg-red-50/40",
        )
      : wrapperClass;
    return (
      <div className={numberWrapperClass}>
        {renderLabel()}
        <NumberInput
          value={val}
          onChange={(v) => onChange(typeof v === "number" ? v : v === "" ? "" : Number(v))}
          min={lower}
          max={upper}
          error={numberError}
          errorMessage={numberError ? numberErrorMsg : ""}
          className={`!py-1.5 !text-sm !rounded-lg !px-3 ${
            numberError ? "!border-red-300 focus:!ring-red-100" : ""
          }`}
          wrapperClassName="!space-y-0"
        />
        {(typeof lower === "number" || typeof upper === "number") && (
          <p
            className={`text-[10px] mt-1 ${
              numberError ? "text-red-600 font-medium" : "text-gray-400"
            }`}
          >
            Aralık: {lower ?? "?"} – {upper ?? "?"}
          </p>
        )}
        {isChanged && originalValue !== undefined && (
          <p className="text-[10px] text-indigo-500 mt-1">
            Önceki: {formatDisplayValue(originalValue)}
          </p>
        )}
      </div>
    );
  }

  // -- Input (string) --
  if (setting.type === "Input") {
    const isPwd = isPasswordRegex(setting.regex);
    const inputType = isPwd && !showPassword ? "password" : "text";
    // Mevcut deger regex'le valid mi? Her render'da hesaplanir.
    let inputError = false;
    if (setting.regex) {
      try {
        inputError = !new RegExp(setting.regex).test(setting.value ?? "");
      } catch (_) {
        inputError = false;
      }
    }
    const inputWrapperClass = inputError
      ? wrapperClass.replace(
          "border-gray-200",
          "border-red-400 ring-2 ring-red-100 bg-red-50/40",
        )
      : wrapperClass;
    const validateAndChange = (e) => {
      const v = e?.target?.value ?? "";
      onChange(v);
    };
    return (
      <div className={inputWrapperClass}>
        {renderLabel()}
        <TextInput
          value={setting.value || ""}
          onChange={validateAndChange}
          type={inputType}
          error={inputError}
          errorMessage={inputError ? getRegexDescription(setting.regex) : ""}
          className={`!py-1.5 !text-sm !rounded-lg !px-3 !shadow-none ${
            inputError ? "!border-red-300 focus:!ring-red-100" : ""
          }`}
          wrapperClassName="!space-y-0"
          endAdornment={
            isPwd ? (
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="text-gray-400 hover:text-gray-600 p-1"
                tabIndex={-1}
              >
                {showPassword ? <FaEyeSlash size={12} /> : <FaEye size={12} />}
              </button>
            ) : null
          }
        />
        {isChanged && originalValue !== undefined && (
          <p className="text-[10px] text-indigo-500 mt-1">
            Önceki: {formatDisplayValue(originalValue)}
          </p>
        )}
      </div>
    );
  }

  // -- Checkbox --
  if (setting.type === "Checkbox") {
    return (
      <div className={wrapperClass}>
        {renderLabel()}
        <Checkbox
          checked={toBool(setting.value)}
          onChange={(e) => onChange(!!e.target.checked)}
          activeLabel="Açık"
          inactiveLabel="Kapalı"
        />
        {isChanged && originalValue !== undefined && (
          <p className="text-[10px] text-indigo-500 mt-1">
            Önceki: {formatDisplayValue(originalValue)}
          </p>
        )}
      </div>
    );
  }

  // -- SelectOptions --
  if (setting.type === "SelectOptions") {
    const options = Array.isArray(setting.options) ? setting.options : [];
    const items = options.map((o) => {
      const id = typeof o === "object" ? (o.id ?? o.value) : o;
      const explicit =
        typeof o === "object" ? (o.label ?? o.value) : null;
      // Once cihazdan gelen label, sonra bilinen Turkce mapping, sonra raw id
      const label =
        explicit ??
        KNOWN_ID_LABELS[id] ??
        String(id);
      return { id, label };
    });
    // CustomDropdown value'yu {id,label} obje bekler — mevcut setting.value'ya
    // karsilik gelen item'i bul; bulunamazsa value'yu hem id hem label olarak
    // sar (oncekiler null ise placeholder gosterir).
    const selectedItem =
      items.find((it) => it.id === setting.value) ||
      (setting.value != null
        ? { id: setting.value, label: String(setting.value) }
        : null);
    return (
      <div className={wrapperClass}>
        {renderLabel()}
        <CustomDropdown
          value={selectedItem}
          onChange={(v) => onChange(v?.id ?? v)}
          options={items}
          showSearch={items.length > 5}
          placeholder="Seçiniz..."
          compact
        />
        {setting.description && (
          <p className="text-[11px] text-amber-700 mt-1">{setting.description}</p>
        )}
        {isChanged && originalValue !== undefined && (
          <p className="text-[10px] text-indigo-500 mt-1">
            Önceki: {formatDisplayValue(originalValue)}
          </p>
        )}
      </div>
    );
  }

  // -- MultiSelect (chip toggle) --
  if (setting.type === "MultiSelect") {
    // Defensive: bazi durumlarda value JSON string olarak gelebilir
    let rawValue = setting.value;
    if (typeof rawValue === "string") {
      try {
        const parsed = JSON.parse(rawValue);
        if (Array.isArray(parsed)) rawValue = parsed;
      } catch (_) {}
    }
    const selected = Array.isArray(rawValue) ? rawValue : [];
    const options = Array.isArray(setting.options) ? setting.options : [];
    const toggle = (opt) => {
      const next = selected.includes(opt)
        ? selected.filter((x) => x !== opt)
        : [...selected, opt];
      onChange(next);
    };
    // Az ve kısa seçenekli MultiSelect (ör. "Su Kazanı Tahliye Valfleri" —
    // 1/2/3) tek sütuna sığar; diğer input kartlarıyla aynı boyutta dursun
    // diye yalnızca çok seçenekli olanlar (log seviyeleri/kategorileri gibi)
    // iki sütun kaplar.
    const isWide = options.length > 4;
    return (
      <div
        className={`${wrapperClass} ${isWide ? "col-span-1 sm:col-span-2" : ""}`}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <label
              className="text-xs font-SemiBold text-gray-700 leading-tight truncate"
              title={setting.label}
            >
              {setting.label}
            </label>
            <span className="text-[10px] text-gray-400">
              ({selected.length}/{options.length})
            </span>
          </div>
          {isChanged && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-indigo-100 text-indigo-700">
              YENİ
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {options.map((opt) => {
            const isOn = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                  isOn
                    ? "bg-indigo-600 text-white border-indigo-600 shadow-sm hover:bg-indigo-700"
                    : "bg-white text-gray-500 border-gray-200 hover:border-indigo-300 hover:text-indigo-600"
                }`}
              >
                {isOn && <FaCheck size={9} />}
                <span>{opt}</span>
              </button>
            );
          })}
        </div>
        {selected.length === 0 && (
          <p className="text-[10px] text-gray-400 mt-1.5">
            Hiçbiri seçili değil — tıklayarak ekleyin
          </p>
        )}
        {isChanged && originalValue !== undefined && (
          <p className="text-[10px] text-indigo-500 mt-1">
            Önceki: {formatDisplayValue(originalValue)}
          </p>
        )}
      </div>
    );
  }

  // -- TeaFilterCups (array of cups) --
  if (setting.type === "TeaFilterCups") {
    const cups = Array.isArray(setting.value) ? setting.value : [];
    const ctx = setting.brewingContext || {};
    const teaActive = !!ctx.teaActive;
    const filterActive = !!ctx.filterActive;
    const cleaningActive = !!ctx.cleaningActive;
    const anyBrewing = teaActive || filterActive || cleaningActive;

    // Tek bardak Cay Referansi, tek bardak Filtre Kahve Referansi olabilir —
    // radio-button mantigi: referans yalnizca BASKA bir bardaga tasinabilir,
    // mevcut referans bardakta tekrar tiklayinca un-toggle olmaz (sistemin
    // referansiz kalmasi anlamsiz).
    const setReference = (idx, refField) => {
      // Brewing aktifken referans degisikligi kilitli
      if (refField === "referenceTea" && teaActive) return;
      if (refField === "referenceFilterCoffee" && filterActive) return;
      const current = cups[idx];
      // Zaten bu bardakta referans varsa hicbir sey yapma (off'a cekmeyi
      // engelle)
      if (current && current[refField]) return;
      const next = cups.map((c, i) => {
        if (i === idx) return { ...c, [refField]: true };
        // Diger bardaklarda ayni referans flag'i kapat (radio gibi)
        if (c[refField]) return { ...c, [refField]: false };
        return c;
      });
      onChange(next);
    };
    const updateCup = (idx, field, fieldValue) => {
      const next = cups.map((c, i) =>
        i === idx ? { ...c, [field]: fieldValue } : c,
      );
      onChange(next);
    };

    // Bardak tolerans field'i aktif demleme nedeniyle kilitli mi?
    const isCupToleranceLocked = (cup) => {
      if (cup.referenceTea && teaActive) return true;
      if (cup.referenceFilterCoffee && filterActive) return true;
      return false;
    };
    return (
      <div
        className={`relative rounded-2xl border bg-gradient-to-br from-emerald-50/30 to-white p-4 col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4 ${
          isChanged
            ? "border-indigo-400 ring-2 ring-indigo-100"
            : "border-emerald-200"
        }`}
      >
        <header className="flex flex-wrap items-center gap-2 mb-3">
          <div className="p-2 rounded-xl bg-emerald-100">
            <FaGlassWhiskey className="text-emerald-700 w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-bold text-emerald-900">
              {setting.label}
            </h4>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Bardak adı, ağırlık ve tolerans değerleri. Çay ve Filtre Kahve
              için ayrı birer referans seçilebilir.
            </p>
          </div>
          {inlineFields.map((f) => {
            const lower = f.setting?.lowerBound;
            const upper = f.setting?.upperBound ?? f.setting?.upperBounde;
            const num = Number(f.setting?.value);
            const outOfRange =
              (typeof lower === "number" && num < lower) ||
              (typeof upper === "number" && num > upper);
            return (
              // Kutu SABİT boyutlu: yazarken, hata çıkarken veya "Önceki"
              // satırı belirirken en/boy DEĞİŞMEZ — başlık zıplamasın.
              // Ayar adı input'un SOLUNDA; sağdaki dar sütun input + tek
              // satırlık durum metnini (aralık / hata / önceki değer) taşır,
              // o satır her zaman yer kapladığı için yükseklik oynamaz.
              <div
                key={f.key}
                className={`shrink-0 w-[300px] h-[62px] flex items-center gap-2 rounded-lg border bg-white px-2.5 overflow-hidden ${
                  f.isChanged
                    ? "border-indigo-400 ring-2 ring-indigo-100"
                    : outOfRange
                      ? "border-red-400 ring-2 ring-red-100"
                      : "border-emerald-200"
                }`}
              >
                <FaBalanceScale className="text-emerald-600 shrink-0" size={13} />
                <span
                  className="flex-1 min-w-0 text-[11px] text-gray-600 leading-tight line-clamp-2"
                  title={f.setting?.label}
                >
                  {f.setting?.label}
                </span>
                <div className="shrink-0 w-[104px]">
                  <input
                    type="number"
                    min={lower}
                    max={upper}
                    value={f.setting?.value ?? ""}
                    disabled={!!f.setting?.disabled}
                    onChange={(e) =>
                      f.onChange(
                        e.target.value === "" ? "" : Number(e.target.value),
                      )
                    }
                    className={`w-full rounded-md border px-2 py-1 text-sm focus:outline-none focus:ring-2 ${
                      outOfRange
                        ? "border-red-400 ring-red-100 bg-white"
                        : "border-gray-200 bg-white focus:ring-emerald-200 focus:border-emerald-400"
                    }`}
                  />
                  {/* Tek satır, her durumda yer kaplar → boyut sabit */}
                  <span
                    className={`block h-3 mt-0.5 text-[10px] leading-tight truncate ${
                      outOfRange
                        ? "text-red-600"
                        : f.isChanged
                          ? "text-indigo-500"
                          : "text-gray-400"
                    }`}
                    title={
                      f.isChanged && f.originalValue !== undefined
                        ? `Önceki: ${formatDisplayValue(f.originalValue)}`
                        : undefined
                    }
                  >
                    {f.isChanged && f.originalValue !== undefined
                      ? `Önceki: ${formatDisplayValue(f.originalValue)}`
                      : typeof lower === "number" || typeof upper === "number"
                        ? `${lower ?? 0} – ${upper ?? "\u221E"}`
                        : "\u00A0"}
                  </span>
                </div>
              </div>
            );
          })}
          {onResetDefaults && (
            <button
              type="button"
              disabled={anyBrewing}
              onClick={onResetDefaults}
              className={`inline-flex items-center gap-1.5 h-[62px] px-3 rounded-lg text-xs font-SemiBold border transition ${
                anyBrewing
                  ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                  : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              }`}
              title={
                anyBrewing
                  ? "Demleme/temizlik aktifken sıfırlanamaz"
                  : "Bardak kayıtlarını makinedeki fabrika değerlerine (2 varsayılan bardak) döndürür — değişiklik kaydedilirken onaylanır"
              }
            >
              <FaUndo size={10} />
              <span>Varsayılana Dön</span>
            </button>
          )}
          {isChanged && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-indigo-100 text-indigo-700">
              YENİ
            </span>
          )}
        </header>

        {anyBrewing && (
          <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2">
            <FaLock className="text-amber-700 mt-0.5 shrink-0" size={11} />
            <div className="text-[11px] text-amber-900 leading-snug">
              <strong className="font-semibold">Demleme/temizlik aktif.</strong>{" "}
              {teaActive && "Çay demlemesi devam ederken çay referans bardağının referans, tolerans ve boş ağırlık değerleri kilitlidir. "}
              {filterActive && "Filtre kahve demlemesi devam ederken filtre kahve referans bardağının referans, tolerans ve boş ağırlık değerleri kilitlidir. "}
              {cleaningActive && "Temizlik aktif. "}
              Diğer alanlar değiştirilebilir.
            </div>
          </div>
        )}

        {cups.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white/60 p-6 text-center">
            <p className="text-sm text-gray-400">Kayıtlı bardak yok</p>
          </div>
        ) : (
          // Bardaklar kutu kutu, yan yana. Dar sütunda üç ağırlık alanı yan
          // yana sığmadığı için kart içinde ALT ALTA diziliyor.
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {cups.map((cup, idx) => {
              const filled = cup.filledWeight ?? 0;
              const empty = cup.emptyWeight ?? 0;
              const lowerTol = cup.lowerTolerance ?? 0;
              const upperTol = cup.upperTolerance ?? 0;
              // Tolerans ve bos agirlik zinciri: lowerTol <= empty <= upperTol
              const lowerExceedsEmpty = lowerTol > empty;
              const emptyExceedsUpper = empty > upperTol;
              const toleranceChainBroken = lowerExceedsEmpty || emptyExceedsUpper;
              // Diger bardaklarin [lowerTol, upperTol] araliklari bu bardagin
              // araligi ile cakisamaz. Ayrica diger bardaklarin emptyWeight'i
              // bu bardagin [lowerTol, upperTol] araliginin icine duseememeli.
              const overlapsOther = cups.some((other, oIdx) => {
                if (oIdx === idx) return false;
                const oLow = other.lowerTolerance ?? 0;
                const oUp = other.upperTolerance ?? 0;
                const oEmpty = other.emptyWeight ?? 0;
                const rangesOverlap = oLow <= upperTol && oUp >= lowerTol;
                const otherEmptyInRange = oEmpty >= lowerTol && oEmpty <= upperTol;
                return rangesOverlap || otherEmptyInRange;
              });
              return (
                <div
                  key={cup.id || idx}
                  className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                >
                  {/* Cup header — bardak ismi sadece goruntulenir; duzenleme
                      makinede ayri akisla yapilir (drawer yok burada) */}
                  <div className="px-3 py-2 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-Bold shrink-0">
                        {idx + 1}
                      </span>
                      <FaGlassWhiskey
                        className="text-gray-400 shrink-0"
                        size={11}
                      />
                      <input
                        type="text"
                        maxLength={30}
                        value={typeof cup.name === "string" ? cup.name : ""}
                        placeholder={`Bardak ${idx + 1}`}
                        onChange={(e) => updateCup(idx, "name", e.target.value)}
                        className={`flex-1 min-w-0 text-sm font-SemiBold text-gray-800 bg-transparent rounded-md border px-2 py-1 focus:outline-none focus:ring-2 ${
                          typeof cup.name === "string" && !cup.name.trim()
                            ? "border-red-400 ring-red-100"
                            : "border-transparent hover:border-gray-300 focus:border-emerald-400 focus:ring-emerald-200"
                        }`}
                        title="Bardak adı"
                      />
                    </div>
                    {/* Referans butonları kendi satırında ve büyük — hangi
                        bardağın referans olduğu tek bakışta görünmeli. */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={teaActive}
                        onClick={() => setReference(idx, "referenceTea")}
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-Bold border transition ${
                          cup.referenceTea
                            ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                            : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        } ${teaActive ? "opacity-50 cursor-not-allowed" : ""}`}
                        title={
                          teaActive
                            ? "Çay demlemesi aktif — değiştirilemez"
                            : "Çay referans bardağı olarak işaretle"
                        }
                      >
                        {teaActive && <FaLock size={10} />}
                        <FaLeaf size={12} />
                        <span>Çay Ref.</span>
                      </button>
                      <button
                        type="button"
                        disabled={filterActive}
                        onClick={() => setReference(idx, "referenceFilterCoffee")}
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-Bold border transition ${
                          cup.referenceFilterCoffee
                            ? "bg-amber-700 text-white border-amber-700 shadow-sm"
                            : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"
                        } ${filterActive ? "opacity-50 cursor-not-allowed" : ""}`}
                        title={
                          filterActive
                            ? "Filtre kahve demlemesi aktif — değiştirilemez"
                            : "Filtre Kahve referans bardağı olarak işaretle"
                        }
                      >
                        {filterActive && <FaLock size={10} />}
                        <FaCoffee size={12} />
                        <span>Filtre Kahve Ref.</span>
                      </button>
                    </div>
                  </div>

                  {/* Weight + tolerance grid.
                      Dolu agırlık (filledWeight) makinede kalibrasyon ile oluşur,
                      panelden değiştirilemez. Düzenlenebilir alanlar:
                      lowerTolerance ≤ emptyWeight ≤ upperTolerance.
                      Bardaklar arası [lowerTol, upperTol] aralıkları çakışamaz. */}
                  <div className="p-3 space-y-3">
                    <div>
                      <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                        <FaWeightHanging size={9} />
                        Ağırlık Profili
                      </div>
                      <div className="space-y-2">
                        <label className="flex flex-col text-[10px] text-gray-500">
                          <span className="flex items-center gap-1">
                            Alt Tolerans (gr)
                            {isCupToleranceLocked(cup) && (
                              <FaLock size={7} className="text-amber-600" />
                            )}
                          </span>
                          <input
                            type="number"
                            min={0}
                            max={empty || 2000}
                            value={lowerTol}
                            disabled={isCupToleranceLocked(cup)}
                            onChange={(e) =>
                              updateCup(idx, "lowerTolerance", Number(e.target.value))
                            }
                            className={`mt-0.5 rounded-md border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                              lowerExceedsEmpty || overlapsOther
                                ? "border-red-400 ring-red-100 bg-white"
                                : isCupToleranceLocked(cup)
                                  ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                                  : "border-gray-200 bg-white focus:ring-emerald-200 focus:border-emerald-400"
                            }`}
                          />
                        </label>
                        <label className="flex flex-col text-[10px] text-gray-500">
                          <span className="flex items-center gap-1">
                            Boş Ağırlık (gr)
                            {isCupToleranceLocked(cup) && (
                              <FaLock size={7} className="text-amber-600" />
                            )}
                          </span>
                          <input
                            type="number"
                            min={lowerTol || 0}
                            max={upperTol || 2000}
                            value={empty}
                            disabled={isCupToleranceLocked(cup)}
                            onChange={(e) =>
                              updateCup(idx, "emptyWeight", Number(e.target.value))
                            }
                            className={`mt-0.5 rounded-md border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                              toleranceChainBroken
                                ? "border-red-400 ring-red-100 bg-white"
                                : isCupToleranceLocked(cup)
                                  ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                                  : "border-gray-200 bg-white focus:ring-emerald-200 focus:border-emerald-400"
                            }`}
                          />
                        </label>
                        <label className="flex flex-col text-[10px] text-gray-500">
                          <span className="flex items-center gap-1">
                            Üst Tolerans (gr)
                            {isCupToleranceLocked(cup) && (
                              <FaLock size={7} className="text-amber-600" />
                            )}
                          </span>
                          <input
                            type="number"
                            min={empty || 0}
                            max={2000}
                            value={upperTol}
                            disabled={isCupToleranceLocked(cup)}
                            onChange={(e) =>
                              updateCup(idx, "upperTolerance", Number(e.target.value))
                            }
                            className={`mt-0.5 rounded-md border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                              emptyExceedsUpper || overlapsOther
                                ? "border-red-400 ring-red-100 bg-white"
                                : isCupToleranceLocked(cup)
                                  ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                                  : "border-gray-200 bg-white focus:ring-emerald-200 focus:border-emerald-400"
                            }`}
                          />
                        </label>
                      </div>
                      {(toleranceChainBroken || overlapsOther) && (
                        <div className="mt-1 space-y-0.5">
                          {lowerExceedsEmpty && (
                            <p className="text-[10px] text-red-600">
                              Alt tolerans boş ağırlıktan büyük olamaz
                            </p>
                          )}
                          {emptyExceedsUpper && (
                            <p className="text-[10px] text-red-600">
                              Boş ağırlık üst toleranstan büyük olamaz
                            </p>
                          )}
                          {overlapsOther && (
                            <p className="text-[10px] text-red-600">
                              Bu bardak [alt tolerans, üst tolerans] aralığı diğer bir bardağın aralığı veya boş ağırlığı ile çakışıyor
                            </p>
                          )}
                        </div>
                      )}
                      <div className="mt-2 flex items-center gap-1 text-[10px] text-gray-400">
                        <FaLock size={8} />
                        <span>
                          Dolu ağırlık: <strong className="text-gray-600">{filled} gr</strong> · makineden kalibre edilir
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // -- GrinderMillTimes (Türk kahvesi öğütücü süreleri — anahtar:saniye obje editörü) --
  // Cihaz { "Küçük": 15, "Büyük": 25 } gibi bir obje + entrySchema gönderir.
  // Anahtar setini makine belirler; panel yalnızca mevcut anahtarların
  // değerlerini düzenler (yeni anahtar eklenmez).
  if (setting.type === "GrinderMillTimes") {
    // Defensive: value bazen JSON string olarak gelebilir
    let obj = setting.value;
    if (typeof obj === "string") {
      try {
        const parsed = JSON.parse(obj);
        if (parsed && typeof parsed === "object") obj = parsed;
      } catch (_) {}
    }
    const entries =
      obj && typeof obj === "object" && !Array.isArray(obj)
        ? Object.keys(obj)
        : [];
    const entrySchema = setting.entrySchema || {};
    const lower = entrySchema.lowerBound;
    const upper = entrySchema.upperBound;
    const entryLabel = entrySchema.label || "Süre (sn)";

    const isEntryInvalid = (v) => {
      if (v === "" || v === null || v === undefined) return true;
      if (typeof v !== "number" || Number.isNaN(v)) return true;
      if (typeof lower === "number" && v < lower) return true;
      if (typeof upper === "number" && v > upper) return true;
      return false;
    };
    const anyInvalid = entries.some((k) => isEntryInvalid(obj[k]));
    const grinderWrapperClass = anyInvalid
      ? wrapperClass.replace(
          "border-gray-200",
          "border-red-400 ring-2 ring-red-100 bg-red-50/40",
        )
      : wrapperClass;
    const updateEntry = (key, v) => {
      const num = typeof v === "number" ? v : v === "" ? "" : Number(v);
      onChange({ ...obj, [key]: num });
    };
    return (
      <div className={grinderWrapperClass}>
        {renderLabel()}
        {entries.length === 0 ? (
          <p className="text-[11px] text-gray-400">
            Tanımlı öğütücü yok — makineden eklenir
          </p>
        ) : (
          <div className="space-y-2">
            {entries.map((key) => {
              const v = obj[key];
              const invalid = isEntryInvalid(v);
              return (
                <div key={key} className="flex items-center gap-2">
                  <span
                    className="text-xs font-medium text-gray-600 truncate w-20 shrink-0"
                    title={key}
                  >
                    {key}
                  </span>
                  <div className="flex-1 min-w-0">
                    <NumberInput
                      value={v}
                      onChange={(nv) => updateEntry(key, nv)}
                      min={lower}
                      max={upper}
                      error={invalid}
                      className={`!py-1.5 !text-sm !rounded-lg !px-3 ${
                        invalid ? "!border-red-300 focus:!ring-red-100" : ""
                      }`}
                      wrapperClassName="!space-y-0"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[10px] text-gray-400 mt-1.5">
          {entryLabel}
          {(typeof lower === "number" || typeof upper === "number") &&
            ` · Aralık: ${lower ?? "?"} – ${upper ?? "?"}`}
        </p>
        {anyInvalid && (
          <p className="text-[10px] text-red-600 font-medium mt-0.5">
            Tüm süreler {lower ?? "?"} – {upper ?? "?"} aralığında olmalı
          </p>
        )}
        {isChanged && originalValue && typeof originalValue === "object" && (
          <p className="text-[10px] text-indigo-500 mt-1">
            Önceki:{" "}
            {Object.entries(originalValue)
              .map(([k, ov]) => `${k}: ${ov}`)
              .join(", ")}
          </p>
        )}
      </div>
    );
  }

  // -- Bilinmeyen tip — JSON fallback --
  return (
    <div className={wrapperClass}>
      {renderLabel()}
      <pre className="text-[10px] text-gray-500 bg-gray-50 p-2 rounded overflow-auto">
        {JSON.stringify(setting.value, null, 2)}
      </pre>
    </div>
  );
}
